"use client";

import type { HTMLAttributes } from "react";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { motion, useAnimation } from "motion/react";

interface BotIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface BotIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  loop?: boolean;
  animateOnMount?: boolean;
}

const BotIcon = forwardRef<BotIconHandle, BotIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 28,
      loop = false,
      animateOnMount = false,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => void controls.start("animate"),
        stopAnimation: () => void controls.start("normal"),
      };
    });

    useEffect(() => {
      if ((loop || animateOnMount) && !isControlledRef.current) {
        void controls.start("animate");
      }
    }, [loop, animateOnMount, controls]);

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("animate");
        } else {
          onMouseEnter?.(e);
        }
      },
      [controls, onMouseEnter, loop],
    );

    const handleMouseLeave = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("normal");
        } else {
          onMouseLeave?.(e);
        }
      },
      [controls, onMouseLeave, loop],
    );

    return (
      <div
        className={twMerge(className)}
        onMouseEnter={(e) => void handleMouseEnter(e)}
        onMouseLeave={(e) => void handleMouseLeave(e)}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 8V4H8" />
          <rect width="16" height="12" x="4" y="8" rx="2" />
          <path d="M2 14h2" />
          <path d="M20 14h2" />

          <motion.line
            x1={15}
            x2={15}
            initial="normal"
            animate={controls}
            variants={{
              normal: { y1: 13, y2: 15 },
              animate: {
                y1: [13, 14, 13], // open → closed → open
                y2: [15, 14, 15],
                transition: {
                  duration: 0.5,
                  ease: "easeInOut",
                  delay: 0.2,
                  repeat: loop ? Number.POSITIVE_INFINITY : 0,
                  repeatType: loop ? "loop" : undefined,
                  repeatDelay: loop ? 1 : 0,
                },
              },
            }}
          />

          <motion.line
            x1={9}
            x2={9}
            initial="normal"
            animate={controls}
            variants={{
              normal: { y1: 13, y2: 15 },
              animate: {
                y1: [13, 14, 13], // open → closed → open
                y2: [15, 14, 15],
                transition: {
                  duration: 0.5,
                  ease: "easeInOut",
                  delay: 0.2,
                  repeat: loop ? Number.POSITIVE_INFINITY : 0,
                  repeatType: loop ? "loop" : undefined,
                  repeatDelay: loop ? 1 : 0,
                },
              },
            }}
          />
        </svg>
      </div>
    );
  },
);

BotIcon.displayName = "Bot";

export { BotIcon };
